import React, { useState, useEffect } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../constants/fonts';

interface NumberStepperInputProps {
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  step?: number;
  isInvalid?: boolean;
  disabled?: boolean;
  testID?: string;
}

export const NumberStepperInput: React.FC<NumberStepperInputProps> = ({
  value,
  onChange,
  min = 1,
  max = 26,
  step = 1,
  isInvalid = false,
  disabled = false,
  testID,
}) => {
  const [textValue, setTextValue] = useState<string>(String(value));
  const [isFocused, setIsFocused] = useState<boolean>(false);

  useEffect(() => {
    setTextValue(String(value));
  }, [value]);

  const handleIncrement = () => {
    if (disabled) return;
    const current = Number(textValue) || value || min;
    const next = Math.min(max, current + step);
    setTextValue(String(next));
    onChange(next);
  };

  const handleDecrement = () => {
    if (disabled) return;
    const current = Number(textValue) || value || min;
    const next = Math.max(min, current - step);
    setTextValue(String(next));
    onChange(next);
  };

  const handleChangeText = (text: string) => {
    setTextValue(text);
    const parsed = parseInt(text, 10);
    if (!isNaN(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed));
      onChange(clamped);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseInt(textValue, 10);
    if (isNaN(parsed) || parsed < min) {
      setTextValue(String(min));
      onChange(min);
    } else if (parsed > max) {
      setTextValue(String(max));
      onChange(max);
    } else {
      setTextValue(String(parsed));
      onChange(parsed);
    }
  };

  const canIncrement = value < max && !disabled;
  const canDecrement = value > min && !disabled;

  return (
    <View
      style={[
        styles.container,
        isFocused && styles.containerFocused,
        isInvalid && styles.containerInvalid,
        disabled && styles.containerDisabled,
      ]}
      testID={testID}
    >
      <TextInput
        style={[styles.input, disabled && styles.inputDisabled]}
        keyboardType="number-pad"
        value={textValue}
        onChangeText={handleChangeText}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        editable={!disabled}
        selectTextOnFocus
      />
      <View style={styles.stepperCol}>
        <TouchableOpacity
          style={[styles.stepperBtn, !canIncrement && styles.stepperBtnDisabled]}
          onPress={handleIncrement}
          disabled={!canIncrement}
          activeOpacity={0.6}
          hitSlop={{ top: 4, bottom: 2, left: 6, right: 6 }}
        >
          <MaterialIcons
            name="arrow-drop-up"
            size={18}
            color={canIncrement ? '#475569' : '#CBD5E1'}
          />
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.stepperBtn, !canDecrement && styles.stepperBtnDisabled]}
          onPress={handleDecrement}
          disabled={!canDecrement}
          activeOpacity={0.6}
          hitSlop={{ top: 2, bottom: 4, left: 6, right: 6 }}
        >
          <MaterialIcons
            name="arrow-drop-down"
            size={18}
            color={canDecrement ? '#475569' : '#CBD5E1'}
          />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#949494',
    borderRadius: 6,
    paddingLeft: 12,
    paddingRight: 4,
    height: 32,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  containerFocused: {
    borderColor: '#005DA6',
    borderWidth: 1.5,
    ...Platform.select({
      web: {
        boxShadow: '0 0 0 2px rgba(0, 93, 166, 0.2)',
      },
    }),
  },
  containerInvalid: {
    borderColor: '#EF4444',
    backgroundColor: '#FEF2F2',
  },
  containerDisabled: {
    backgroundColor: '#F8F8F8',
    borderColor: '#EBEBEB',
  },
  input: {
    flex: 1,
    height: '100%',
    fontFamily: FontFamily.medium,
    fontSize: 14,
    color: '#2D2D2D',
    paddingVertical: 0,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  inputDisabled: {
    color: '#94A3B8',
  },
  stepperCol: {
    width: 20,
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepperBtn: {
    height: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepperBtnDisabled: {
    opacity: 0.4,
  },
});
